import { flatten, values } from 'lodash-es';
import { utcToZonedTime } from 'date-fns-tz';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function preventDefault(handler) {
    return (event, ...args) => {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        if (handler) {
            return handler(event, ...args);
        }
    };
}
/**
 * Parses phone number input and returns it in a consistent format.
 *
 * @param {string} number
 * @return {string} formatted phone number
 */
export function formatPhoneNumber(number) {
    if (!number) {
        return '';
    }
    let digits = number.replace('/[^0-9+]/', '');
    if (digits.length === 11 && digits.substr(0, 1) === '1') {
        digits = digits.substr(1);
    }
    else if (digits.length === 12 && digits.substr(0, 2) === '+1') {
        digits = digits.substr(2);
    }
    if (digits.length === 10) {
        return '(' + digits.substr(0, 3) + ') ' + digits.substr(3, 3) + '-' + digits.substr(6);
    }
    else if (digits.length === 7) {
        return digits.substr(0, 3) + '-' + digits.substr(3);
    }
    else {
        return digits;
    }
}
/**
 * Get area code from a phone number
 *
 * @param {string} number
 * @return {string} area code
 */
export function getAreaCode(number) {
    var _a;
    if (!number) {
        return '';
    }
    const findings = /^(?:[0-9+]{2}(\d{3})\d{7}|[0-9+]{3}(\d{3})\d{7})$/g.exec(number);
    return findings ?
        ((_a = findings[1]) !== null && _a !== void 0 ? _a : findings[2]) :
        '';
}
export function getErrorMessagesFromResponse(error) {
    if (error.response) {
        const data = error.response.data;
        return (data === null || data === void 0 ? void 0 : data.errors) ? flatten(values(data.errors)) : [(data === null || data === void 0 ? void 0 : data.message) || error.message];
    }
    else if (error.request) {
        return ['Error performing the request'];
    }
    else {
        return [error.message];
    }
}
/* Format money in consistent way
*
* @param {number} quantity
* @return {string}
*/
export function formatMoney(quantity) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(quantity);
}
export function formatDate(isoDate) {
    const tz = getTenantTimeZone();
    const date = utcToZonedTime(new Date(isoDate), tz);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const formattedDate = `${month}/${day}/${year}`;
    return formattedDate;
}
export function getTenantTimeZone() {
    var _a;
    return ((_a = getPageData('app-settings')) === null || _a === void 0 ? void 0 : _a.timezone) || 'America/Chicago';
}
/**
 * Load the JSON data embedded in a script tag on the page.
 *
 * @param {string} id element ID
 * @return {*} parsed data
 */
export const getPageData = (id) => {
    try {
        const script = document.querySelector(`script[id='${id}']`);
        if (!script) {
            throw new Error(`#${id} DOM element not found!`);
        }
        return JSON.parse(script.innerHTML);
    }
    catch (error) {
        throw new Error(`Unable to load ${id} data: ${error.message}`);
    }
};
/**
 * Save the JSON data in a script tag on the page.
 *
 * @param {string} id element ID
 * @param {string} data data to store
 */
export const setPageData = (id, data) => {
    try {
        const script = document.querySelector(`script[id='${id}']`);
        if (!script) {
            throw new Error(`#${id} DOM element not found!`);
        }
        script.innerHTML = JSON.stringify(data);
    }
    catch (error) {
        throw new Error(`Unable to set ${id} data: ${error.message}`);
    }
};
// Immutabily insert a value into an array
export function arrayInsertAt(array, index, value) {
    const newArray = array.concat();
    newArray.splice(index, 0, value);
    return newArray;
}
// Immutabily update a value in an array
export function arrayUpdateAt(array, index, value) {
    const newArray = array.concat();
    newArray[index] = value;
    return newArray;
}
// Immutabily remove a value from an array
export function arrayRemoveAt(array, index) {
    const newArray = array.concat();
    newArray.splice(index, 1);
    return newArray;
}
export const remainingPercentage = (percentage) => 100 - percentage;
export function isStripeError(error) {
    return (typeof error === 'object' && error !== null && 'message' in error);
}
/**
 * Convert an ArrayBuffer to a base64 string
 * @param {ArrayBuffer | null} buffer
 * @return {string | null}
 */
export const arrayBufferToBase64 = (buffer) => {
    if (buffer === null)
        return buffer;
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};
/**
 * Convert a base64 string to a Uint8Array
 * @param {string} base64String
 * @return {Uint8Array}
 */
export const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};
